import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    inject,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {ConfirmationDialogData, ReplyData, ReplyEvent} from '../../_types/shared';
import {User} from '../../_types/user';
import {AuthService} from '../../_services/auth.service';
import {FormControl} from '@angular/forms';
import {Post, PostAttachment, PostAttachmentType, PostComment, PostNewComment} from '../../_types/content';
import {firstValueFrom, take} from 'rxjs';
import {SharedStateService} from '../../_services/shared-state.service';
import {ContentService} from '../../_services/content.service';
import {MatSnackBar} from "@angular/material/snack-bar";
import {Community, UserAccessLevel} from "../../_types/communities";
import {LocalStorageService} from "../../_services/local-storage.service";
import {SnackbarService} from '../../_services/snackbar.service';
import {CommunitiesService} from "../../_services/communities.service";
import {ChoiceWithIndices} from 'ngx-mentions';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {SelectUserComponent} from "../select-user/select-user.component";
import {SocketService} from "../../_services/socket.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {DirectUploadService, PostUploadHandle} from '../../_services/direct-upload.service';
import {throttleTime} from "rxjs/operators";

@Component({
    selector: 'app-reply-field',
    templateUrl: './reply-field.component.html',
    styleUrl: './reply-field.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReplyFieldComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() isReply: boolean = false;
    @Input() isOpenState: boolean = false;
    @Input() initialMention: any | null = null;
    @Input() prefillComment?: string;
    @ViewChild('replyField') replyField: any;
    @Input({required: true}) post!: Post;
    @Output() action: EventEmitter<ReplyEvent<ReplyData>> = new EventEmitter<ReplyEvent<ReplyData>>();
    comment: FormControl<string | null> = new FormControl('');
    attachmentsValid: boolean = true;
    attachment: any;
    attachmentType: string = 'text';
    attachmentInitial: boolean = false;
    is_anonymous: boolean = false;
    placeholder: string = '';
    selectedCommunity: Community | null = null;
    currentUser: User | null = null;
    choices: User[] = [];
    isMobile: boolean = window.innerWidth <= 1000;
    mentions: ChoiceWithIndices[] = [];
    searchRegexp = new RegExp('^([-&.\\w]+ *){0,3}$');
    commentAsUser: { id: number, username: string } | undefined;
    selectedUsers: ChoiceWithIndices[] = [];

    mentionsConfig = [
        {
            triggerCharacter: '@',
            getChoiceLabel: (user: User): string => {
                return `@${user.username}`;
            },
        },
    ]

    userSearchControl: FormControl = new FormControl();
    filteredUsers: any[] = [];
    typingTimer: any;
    loadingUsers: boolean = false;
    hideAttachments: boolean = false;
    community: Community | null = null;

    sending = false;
    private readonly directUpload = inject(DirectUploadService);
    private readonly uploads = new Map<File, PostUploadHandle>();
    private attachmentSelected = false;
    private attachmentVersion = 0;
    private destroyed = false;
    private keepAlive = false;

    get uploadProgress(): number | null {
        const total = [...this.uploads.keys()].reduce((sum, file) => sum + file.size, 0);
        if (!total) return null;
        return Math.min(100, Math.floor(100 * [...this.uploads].reduce((sum, [file, handle]) => sum + file.size * handle.progress(), 0) / total));
    }

    private stopUploads(): void {
        for (const handle of this.uploads.values()) void handle.abort();
        this.uploads.clear();
        this.stopKeepAlive();
    }

    private stopKeepAlive(): void {
        if (this.keepAlive) this.authService.stopTimeoutDisable();
        this.keepAlive = false;
    }

    private syncUploads(): void {
        const files: File[] = this.attachmentInitial ? [] : (Array.isArray(this.attachment) ? this.attachment : [this.attachment]).filter(value => value instanceof File);
        for (const [file, handle] of this.uploads) {
            if (!files.includes(file)) {
                void handle.abort();
                this.uploads.delete(file);
            }
        }
        if (files.length && !this.keepAlive) {
            this.authService.disableTimeout(4 * 60 * 60 * 1000);
            this.keepAlive = true;
        }
        for (const file of files) {
            if (!this.uploads.has(file) && this.post?.community) {
                const handle = this.directUpload.uploadPost(file, this.post.community, 'comment');
                this.uploads.set(file, handle);
                void handle.done.catch(error => {
                    if (!this.destroyed && this.uploads.get(file) === handle && !this.sending) {
                        this.openSnackBar(error instanceof Error ? error.message : String(error), 'error');
                    }
                });
            }
        }
        if (!files.length) this.stopKeepAlive();
    }

    private async prepareUploads(data: PostNewComment, type: string): Promise<boolean> {
        const version = this.attachmentVersion;
        delete data.attachment_files;
        const originals = this.initialCommentData?.attachments ?? [];
        const value = type === 'gif_url' ? this.attachment?.embed_url ?? this.attachment : this.attachment;
        const unchanged = !this.attachmentSelected || this.attachmentInitial || originals.some(a => a.attachment_type === type && typeof value === 'string' && a.url === value);
        if (unchanged) {
            delete data.video_urls;
            delete data.gif_urls;
            delete data.url;
        } else if (originals.length) {
            data.attachments_to_delete = String(originals[0].id);
        }
        this.syncUploads();
        try {
            if (!this.post?.community && (this.attachment instanceof File || Array.isArray(this.attachment))) {
                throw new Error('Cannot upload attachments without the post community.');
            }
            const states = await Promise.all([...this.uploads.values()].map(handle => handle.done));
            if (this.destroyed || version !== this.attachmentVersion) {
                this.sending = false;
                this.cdr.markForCheck();
                return false;
            }
            if (states.length) data.direct_upload_ids = states.map(state => state.upload_id).join(',');
            return true;
        } catch (error) {
            this.stopUploads();
            this.sending = false;
            if (!this.destroyed) this.openSnackBar(error instanceof Error ? error.message : String(error), 'error');
            this.cdr.markForCheck();
            return false;
        }
    }

    constructor(protected readonly authService: AuthService,
                private readonly sharedStateService: SharedStateService,
                private readonly contentService: ContentService,
                private readonly snackBar: MatSnackBar,
                private readonly localStorageService: LocalStorageService,
                private readonly snackbarService: SnackbarService,
                private readonly socketService: SocketService,
                private readonly communitiesService: CommunitiesService,
                private readonly cdr: ChangeDetectorRef,
                private readonly dialog: MatDialog) {
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });

        this.comment.valueChanges.pipe(
            throttleTime(30 * 1000, undefined, {leading: true, trailing: true}),
            takeUntilDestroyed(),
        ).subscribe((): void => {
            // refresh user when activity on form
            void this.authService.refreshUser().catch(() => {});
        });

        this.userSearchControl.valueChanges.subscribe(
            (value): void => {
                clearTimeout(this.typingTimer);
                this.typingTimer = setTimeout((): void => {
                    this.loadingUsers = true;
                    let communityId: number | undefined = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null)?.id;
                    if (communityId) {
                        this.communitiesService.searchSimpleUserList(communityId, value.toLowerCase()).subscribe(r => {
                            this.loadingUsers = false;
                            this.filteredUsers = r.filter((item: any, index: any, self: any): boolean => {
                                // Remove duplicate items
                                return self.indexOf(item) === index;
                            }).filter((item: any) => {
                                // Only include moderators
                                return item.is_moderator === true;
                            }).map((option: any) => option.user);
                        }, e => {
                            this.snackbarService.openErrorSnackBar(e,)
                            this.loadingUsers = false;
                            this.filteredUsers = []
                        })
                    } else {
                        this.loadingUsers = false;
                        this.filteredUsers = [];
                    }
                }, 500);
            }
        );
    }

    private _initialCommentData?: PostComment;

    get initialCommentData(): PostComment | undefined {
        return this._initialCommentData;
    }

    @Input() set initialCommentData(value: PostComment) {
        this._initialCommentData = value;
        this.is_anonymous = !!value?.is_anonymous;
        this.comment.patchValue(value.text);
    };

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    get initialCommentDataAttachment(): PostAttachment | null {
        return this._initialCommentData?.attachments?.[0] || null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isReply'] || changes['isOpenState']) {
            this.placeholder = this.isReply ? 'Write a reply...' : !this.isOpenState ? 'Add a comment' : 'Write a comment...'
        }
    }

    async ngOnInit(): Promise<void> {
        this.selectedCommunity = this.localStorageService.getItem<Community | null>(
            'lastSelectedCommunity',
            null
        );
        this.currentUser = this.authService.getUser();

    }

    ngAfterViewInit(): void {
        if (this.initialMention) {
            let mentionText: string = "@" + this.initialMention.username + ' '
            this.comment.patchValue(mentionText);
            this.addMentionsFromText(mentionText).then(r => {
                this.selectedUsers = r;
                this.mentions = r;
            })
            // Focus and set cursor
            setTimeout((): void => {
                if (this.replyField && this.replyField.nativeElement) {
                    this.replyField.nativeElement.focus();
                    const mentionTextLength = `@${this.initialMention.username} `.length;
                    this.replyField.nativeElement.setSelectionRange(mentionTextLength, mentionTextLength);
                }
            }, 0);
            this.cdr.detectChanges();
        } else if (this._initialCommentData?.text) {
            this.addMentionsFromText(this._initialCommentData?.text).then(r => {
                this.selectedUsers = r;
                this.mentions = r;
            })
        }
        if (this.replyField?.nativeElement) {
            this.socketService.watchTyping(
                this.post?.id,
                this.replyField.nativeElement
            );
            if (this.prefillComment) {
                this.commentFocus(this.replyField.nativeElement);
            }
        }

        if (this.prefillComment) {
            this.comment.patchValue(this.prefillComment);
            this.commentFocus(this.replyField.nativeElement);
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.attachmentVersion++;
        this.stopUploads();
        this.socketService.stopWatchingTyping(this.post?.id);
    }

    addMentionsFromText(text: string): Promise<any[]> {
        const mentions: any[] = [];
        const regex = /@(\S+)/g;
        const matches = Array.from(text.matchAll(regex));

        if (!this.selectedCommunity?.id) {
            return Promise.resolve([]);
        }

        const promises = matches.map(match => {
            const fullMatch: string = match[0];
            const name: string = match[1];
            const startIndex: number = match.index || 0;
            const endIndex: number = startIndex + fullMatch.length;

            return this.communitiesService.searchSimpleUserList(this.selectedCommunity!.id, name.toLowerCase())
                .toPromise()
                .then(r => {
                    if (r && r[0]) {
                        mentions.push({
                            choice: {
                                id: r[0]?.user?.id,
                                name: name,
                            },
                            indices: {
                                start: startIndex,
                                end: endIndex,
                                triggerCharacter: '@',
                            }
                        });
                    }
                })
                .catch(e => {
                    console.error(e);
                    this.openSnackBar(e || 'An error occurred please try again later', 'error');
                });
        });

        return Promise.all(promises).then(() => mentions);
    }


    async loadChoices({
                          searchText,
                          triggerCharacter,
                      }: {
        searchText: string;
        triggerCharacter: string;
    }): Promise<User[]> {
        if (!this.selectedCommunity?.id) {
            return [];
        }
        try {
            const r = await firstValueFrom(
                this.communitiesService.searchSimpleUserList(this.selectedCommunity.id, searchText)
            );
            this.choices = r.filter((item: any, index: any, self: any): boolean => {
                // Remove duplicate items
                return self.indexOf(item) === index;
            }).map((option: any) => option.user);
            return this.choices;
        } catch (e) {
            console.error(e);
            return [];
        }
    }


    commentFocus(replyField: HTMLTextAreaElement): void {
        if (this.isMobile || this.prefillComment) {
            setTimeout((): void => {
                replyField.scrollIntoView(true)
            }, 500);
        }
        this.setState(true)
    }

    setState(state: boolean): void {
        this.isOpenState = state;
    }

    attachmentsValidEmitter(event: boolean): void {
        this.attachmentsValid = event
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    getAttachmentField(event: any): void {
        this.attachmentType = event.type;
        this.attachment = event.value;
        this.attachmentInitial = event.initial;
        this.attachmentSelected = true;
        this.attachmentVersion++;
        this.syncUploads();
        if (this.attachmentType == 'text') {
            this.attachmentsValid = true;
        }
    }

    reset(): void {
        this.attachmentVersion++;
        this.stopUploads();
        this.sending = false;
        this.attachment = null;
        this.attachmentType = 'text';
        this.attachmentInitial = false;
        this.attachmentSelected = false;
        if (this.isReply) this.sharedStateService.removeVariable('replyCommentId');
        this.comment.reset('');
        this.selectedUsers = [];
        this.mentions = [];
        this.setState(false);
        this.action.emit({action: 'cancel', data: {comment: this.initialCommentData!} as any});
        this.commentAsUser = undefined
        this.hideAttachments = true;
        setTimeout((): void => {
            this.hideAttachments = false;
            this.cdr.detectChanges();
        }, 0);
        this.cdr.detectChanges();
    }

    async submitComment(): Promise<void> {
        if (this.sending) return;
        this.sending = true;
        let commentData: PostNewComment;
        let type: PostAttachmentType | 'text';

        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';

                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }
        commentData = this.addAttachmentToPayload(type) as PostNewComment;
        const replyId = this.isReply && this.sharedStateService.getCurrentValue('replyCommentId');
        if (replyId) {
            commentData = {
                ...commentData,
                parent: replyId
            };
        }
        if (this.is_anonymous) {
            commentData = {
                ...commentData,
                is_anonymous: this.is_anonymous
            };
        }
        if (this.mentions.length > 0) {
            let mentioned_users = [];
            for (let mention of this.mentions) {
                mentioned_users.push(mention.choice.id)
            }
            commentData = {
                ...commentData,
                mentioned_users
            };
        }
        if (this.commentAsUser?.id) {
            commentData = {
                ...commentData,
                comment_as: this.commentAsUser?.id
            };
        }

        if (!await this.prepareUploads(commentData, type)) return;
        this.contentService.newPostComments(this.post.id, type, commentData).pipe(take(1)).subscribe(() => {
            this.uploads.clear();
            this.stopKeepAlive();
            this.replyField?.nativeElement.focus();
            this.reset();
            this.contentService.getPostComments(this.post.id).pipe(take(1)).subscribe({
                next: res => {
                    this.post.comments_count = res.length;
                    this.sharedStateService.updateModalComments(res);
                },
                error: () => this.openSnackBar('Comment saved, but the comments could not be refreshed. Please reload.', 'error'),
            });
        }, error => {
            this.sending = false;
            this.stopKeepAlive();
            this.cdr.markForCheck();
            this.openSnackBar(error || 'An error occurred please try again later', 'error');
        });
    }

    async patchComment(): Promise<void> {
        if (this.sending) return;
        this.sending = true;
        let commentData: PostNewComment;
        let type: PostAttachmentType | 'text';

        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';

                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }
        commentData = this.addAttachmentToPayload(type);

        const replyId = this.isReply && this.sharedStateService.getCurrentValue('replyCommentId');
        if (replyId) {
            commentData = {
                ...commentData,
                parent: replyId,
            };
        }

        commentData = {
            ...commentData,
            is_anonymous: this.is_anonymous,
        };

        if (!await this.prepareUploads(commentData, type)) return;

        this.contentService.patchComment(this.post.id, this.initialCommentData?.id!, commentData, type).subscribe(r => {
            this.uploads.clear();
            this.stopKeepAlive();
            this.action.emit({action: 'submit', data: {comment: r.data} as any});
            this.snackbarService.openSuccessSnackBar('Comment has been updated');
            this.reset();
        }, e => {
            this.sending = false;
            this.stopKeepAlive();
            this.cdr.markForCheck();
            this.snackbarService.openErrorSnackBar(e);
        })
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    validate(formControl: FormControl): void {
        formControl.markAllAsTouched();
    }

    getDisplayLabel = (item: User): string => {
        return (item as User).username
    };

    onSelectedChoicesChange(choices: ChoiceWithIndices[]): void {
        if (this.initialMention && !this._initialCommentData?.text) {
            this.mentions = choices;
        } else {
            // Without this it just removes the premade ones
            this.addMentionsFromText(this.comment.value || '').then(r => {
                this.mentions = r;
            })
        }
    }

    selectUser(event: any): void {
        this.commentAsUser = event
        this.userSearchControl.patchValue('');
    }

    startMention(): void {
        const dialogRef: MatDialogRef<SelectUserComponent> = this.dialog.open<SelectUserComponent, ConfirmationDialogData>(SelectUserComponent);
        dialogRef.afterClosed().subscribe((resp): void => {
            if (resp) {
                let text = this.comment.value;
                if (text && text.substr(text.length - 1) !== ' ') {
                    text = text + ' '
                }

                let mentionText: string = "@" + resp.username + ' '
                this.replyField.nativeElement.focus();
                this.comment.patchValue(text + mentionText);

                this.addMentionsFromText(this.comment.value || '').then(r => {
                    this.mentions = r;
                });
                this.cdr.detectChanges();
            }
        });
    }

    private addAttachmentToPayload(type: PostAttachmentType | 'text'): PostNewComment {
        if (type === 'video_url') {
            return {
                text: this.comment.value!,
                video_urls: this.attachment
            };
        } else if (type === 'gif_url') {
            return {
                text: this.comment.value!,
                gif_urls: this.attachment?.embed_url ?? this.attachment
            };
        } else if (type == 'url') {
            return {
                text: this.comment.value!,
                url: this.attachment
            };
        } else if (type !== 'text') {
            return {
                text: this.comment.value!,
                attachment_files: this.attachment
            };
        } else {
            return {
                text: this.comment.value!,
            }
        }
    }
}
